<script setup lang="ts">
export interface AccordionPanelProps {
  idx: number
  active: boolean
  icon?: boolean
}

withDefaults(defineProps<AccordionPanelProps>(), {
  icon: true
})
const emit = defineEmits(['expand'])

const id = useId()
const isAnimating = ref(false)

const expand = () => {
  if (isAnimating.value) {
    return
  }
  emit('expand')
}

function setHeight(el: HTMLElement) {
  isAnimating.value = true
  el.style.height = el.scrollHeight + 'px'
}

function unsetHeight(el: HTMLElement) {
  isAnimating.value = false
  el.style.height = ''
}
</script>

<template>
  <div class="w-full border-t border-black">
    <button
      type="button"
      class="flex w-full cursor-pointer items-center justify-between py-3"
      :aria-controls="`acc-${id}_panel-${idx}`"
      :aria-expanded="active"
      @click="expand"
    >
      <span class="inline-block"><slot name="title" /></span>
      <span v-if="icon" class="inline-block w-10 shrink-0">
        <Icon size="24" :name="active ? 'custom:chevron-up' : 'custom:chevron-down'" />
      </span>
    </button>
    <Transition
      name="accordion"
      @enter="setHeight"
      @after-enter="unsetHeight"
      @before-leave="setHeight"
      @after-leave="unsetHeight"
    >
      <div v-show="active" :id="`acc-${id}_panel-${idx}`" :aria-hidden="!active">
        <slot name="content" />
      </div>
    </Transition>
  </div>
</template>

<style lang="postcss">
.accordion-enter-active,
.accordion-leave-active {
  will-change: height, opacity;
  transition:
    height 300ms ease-out,
    opacity 300ms ease-out;
  overflow: hidden;
}

.accordion-enter-from,
.accordion-leave-to {
  height: 0 !important;
  opacity: 0;
}
</style>
